import { Fragment, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  FileCheck,
  FileText,
  FileX,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useCostingMovementReport, useCostingMovements } from "~/hooks/costing";
import { useOilTypes, useRawMaterials } from "~/hooks/purchasing";
import { useReservedNotDispatched } from "~/hooks/wms/useProductStock";
import { supabase } from "~/integrations/supabase/client";

import {
  ITEM_KIND_LABELS,
  MOVEMENT_CLASS_LABELS,
  OIL_REPORT_QTY_FIELDS,
  PdfViewerDialog,
  PeriodSelector,
  fmtCLP,
  fmtQty,
  oilLedgerRowToKg,
  oilRowToKg,
  useCosteoPeriod,
  useOilDensities,
} from "./components/CosteoShared";

interface StockComparisonRow {
  key: string;
  kind: string;
  itemKind: string;
  code: string | null;
  name: string | null;
  theoretical: number;
  system: number;
  diff: number;
  // Bridge inputs (finished goods only): explain teórico − físico.
  reserved: number; // facturado no despachado (teórico ya lo restó, físico no)
  returns: number; // devoluciones NC del período (teórico las reingresa, físico no)
}

// One line of the físico → teórico reconciliation bridge.
function BridgeRow({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: number;
  tone?: "muted" | "strong" | "warn";
}) {
  const warnOn = tone === "warn" && Math.abs(value) > 0.5;
  const cls =
    tone === "strong"
      ? "font-semibold text-foreground"
      : warnOn
        ? "font-medium text-amber-700"
        : "text-muted-foreground";
  return (
    <div className={`flex items-center justify-between gap-6 ${cls}`}>
      <dt>{label}</dt>
      <dd className="tabular-nums">{fmtQty(value)}</dd>
    </div>
  );
}

function useProductStocks() {
  return useQuery({
    queryKey: ["costing_product_stocks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, sku, short_name, stock")
        .is("deleted_at", null);

      if (error) throw error;
      return data;
    },
    staleTime: 60 * 1000,
  });
}

export function CosteoConciliacionPage() {
  const navigate = useNavigate();
  const { periods, period, periodRow, setPeriod } = useCosteoPeriod();

  const { data: report = [] } = useCostingMovementReport(period);
  const { data: movements = [] } = useCostingMovements(periodRow?.id);
  const { data: rawMaterials = [] } = useRawMaterials();
  const { data: oilTypes = [] } = useOilTypes();
  const { data: products = [] } = useProductStocks();
  const { data: densities } = useOilDensities();
  const { data: reservedMap } = useReservedNotDispatched();

  const [viewingPdf, setViewingPdf] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  // A) Negative theoretical inventory — missing opening or purchases.
  const negativeRows = useMemo(
    () => report.filter((r) => Number(r.closing_qty) < 0),
    [report]
  );
  // Aceites shown in kg (liters × density); filters/counts stay unit-agnostic.
  const displayNegatives = useMemo(
    () =>
      negativeRows.map((r) =>
        r.item_kind === "oil_type"
          ? oilRowToKg(
              r,
              densities?.[r.oil_type_id ?? ""],
              OIL_REPORT_QTY_FIELDS
            )
          : r
      ),
    [negativeRows, densities]
  );

  // B) Movements still without cost (WAC not assigned or zero-cost inflows).
  const noCostRows = useMemo(
    () =>
      movements.filter(
        (m) =>
          Number(m.unit_cost_clp) === 0 &&
          ["consumption", "sale", "production", "waste"].includes(
            m.movement_class
          )
      ),
    [movements]
  );
  const displayNoCost = useMemo(
    () =>
      noCostRows.map((m) =>
        m.item_kind === "oil_type"
          ? oilLedgerRowToKg(m, densities?.[m.oil_type_id ?? ""])
          : m
      ),
    [noCostRows, densities]
  );

  // C) Theoretical closing vs live system stock.
  const stockComparison = useMemo<StockComparisonRow[]>(() => {
    const rows: StockComparisonRow[] = [];

    for (const r of report) {
      let system: number | null = null;
      if (r.item_kind === "raw_material") {
        const rm = rawMaterials.find((x) => x.id === r.raw_material_id);
        system = rm ? Number(rm.current_stock) : null;
      } else if (r.item_kind === "oil_type") {
        const ot = oilTypes.find((x) => x.id === r.oil_type_id);
        system = ot ? Number(ot.current_stock_liters) : null;
      } else {
        const p = products.find((x) => x.id === r.product_id);
        system = p ? Number(p.stock) : null;
      }
      if (system === null) continue;

      const theoretical = Number(r.closing_qty);
      const diff = theoretical - system;
      if (Math.abs(diff) > 0.5) {
        // Aceites: both theoretical (closing_qty) and system (current_stock_liters)
        // are liters — filter in liters, then present the row in kg (× density).
        const density =
          r.item_kind === "oil_type"
            ? densities?.[r.oil_type_id ?? ""]
            : undefined;
        const f = density && density > 0 ? density : 1;
        rows.push({
          key: `${r.item_kind}-${r.raw_material_id ?? r.oil_type_id ?? r.product_id}`,
          kind: ITEM_KIND_LABELS[r.item_kind],
          itemKind: r.item_kind,
          code: r.item_code,
          name: r.item_name,
          theoretical: theoretical * f,
          system: system * f,
          diff: diff * f,
          reserved:
            r.item_kind === "finished_good"
              ? (reservedMap?.get(r.product_id ?? "") ?? 0)
              : 0,
          returns: Number(r.returns_qty) || 0,
        });
      }
    }
    return rows.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  }, [report, rawMaterials, oilTypes, products, densities, reservedMap]);

  // D) Purchase documents — with/without supporting PDF.
  const purchaseDocs = useMemo(
    () => movements.filter((m) => m.movement_class === "purchase"),
    [movements]
  );
  const missingDocs = purchaseDocs.filter((m) => !m.attachment_url);

  return (
    <PageContainer>
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/costeo")}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <FileCheck className="h-6 w-6" />
            Conciliación
          </h1>
          <p className="text-muted-foreground">
            Detecte la causa real de cada desviación: inventario teórico vs
            sistema, movimientos sin costo y documentos de respaldo
          </p>
        </div>
        <PeriodSelector periods={periods} value={period} onChange={setPeriod} />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className={negativeRows.length > 0 ? "border-amber-300" : ""}>
          <CardHeader className="pb-2">
            <CardDescription>Inventario teórico negativo</CardDescription>
            <CardTitle className="text-2xl">{negativeRows.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={noCostRows.length > 0 ? "border-amber-300" : ""}>
          <CardHeader className="pb-2">
            <CardDescription>Movimientos sin costo</CardDescription>
            <CardTitle className="text-2xl">{noCostRows.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={stockComparison.length > 0 ? "border-amber-300" : ""}>
          <CardHeader className="pb-2">
            <CardDescription>Diferencias teórico vs sistema</CardDescription>
            <CardTitle className="text-2xl">{stockComparison.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card className={missingDocs.length > 0 ? "border-amber-300" : ""}>
          <CardHeader className="pb-2">
            <CardDescription>Compras sin documento</CardDescription>
            <CardTitle className="text-2xl">
              {missingDocs.length}/{purchaseDocs.length}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="negativos">
        <TabsList className="h-9">
          <TabsTrigger value="negativos">Inventario Negativo</TabsTrigger>
          <TabsTrigger value="sincosto">Sin Costo</TabsTrigger>
          <TabsTrigger value="stock">Teórico vs Sistema</TabsTrigger>
          <TabsTrigger value="documentos">Documentos</TabsTrigger>
        </TabsList>

        {/* A) Negative closings */}
        <TabsContent value="negativos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Ítems con inventario teórico negativo
              </CardTitle>
              <CardDescription>
                Generalmente indica inventario inicial sin cargar, compras
                faltantes o consumos mal imputados.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ítem</TableHead>
                    <TableHead className="w-44">Tipo</TableHead>
                    <TableHead className="text-right">Entradas</TableHead>
                    <TableHead className="text-right">Salidas</TableHead>
                    <TableHead className="text-right">Final Teórico</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {negativeRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-6 text-center text-muted-foreground"
                      >
                        Sin inventarios negativos — todo cuadra ✓
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayNegatives.map((r, index) => (
                      <TableRow
                        key={`${r.item_kind}-${r.item_code}`}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                      >
                        <TableCell>
                          <div className="font-medium">{r.item_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.item_code}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {ITEM_KIND_LABELS[r.item_kind]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {fmtQty(r.availability_qty + r.returns_qty)}
                        </TableCell>
                        <TableCell className="text-right">
                          {fmtQty(
                            r.consumption_qty +
                              r.sales_qty +
                              r.waste_qty +
                              r.adjustments_out_qty
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium text-destructive">
                          {fmtQty(r.closing_qty)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* B) Movements without cost */}
        <TabsContent value="sincosto">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Movimientos sin costo asignado
              </CardTitle>
              <CardDescription>
                Salidas o producción con costo 0 — falta cargar
                compras/inventario inicial del ingrediente, o ejecutar
                “Sincronizar y Recalcular”.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Fecha</TableHead>
                    <TableHead>Ítem</TableHead>
                    <TableHead className="w-32">Clase</TableHead>
                    <TableHead className="w-32">Documento</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {noCostRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-6 text-center text-muted-foreground"
                      >
                        Todos los movimientos tienen costo ✓
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayNoCost.map((m, index) => (
                      <TableRow
                        key={m.id}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                      >
                        <TableCell>
                          {dayjs(m.movement_date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {m.raw_materials?.name ??
                              m.oil_types?.name ??
                              m.products?.short_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {m.raw_materials?.code ??
                              m.oil_types?.code ??
                              m.products?.sku}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {MOVEMENT_CLASS_LABELS[m.movement_class]}
                          </Badge>
                        </TableCell>
                        <TableCell>{m.document_number ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          {fmtQty(m.quantity)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* C) Theoretical vs system stock */}
        <TabsContent value="stock">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Inventario teórico vs stock del sistema
              </CardTitle>
              <CardDescription>
                Diferencias entre el inventario final teórico del costeo y el
                stock actual del sistema (se omiten diferencias ≤ 0,5). Los
                productos terminados se pueden expandir para ver el puente
                físico → teórico.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ítem</TableHead>
                    <TableHead className="w-44">Tipo</TableHead>
                    <TableHead className="text-right">
                      Teórico (Costeo)
                    </TableHead>
                    <TableHead className="text-right">Sistema</TableHead>
                    <TableHead className="text-right">Diferencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockComparison.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-6 text-center text-muted-foreground"
                      >
                        Sin diferencias relevantes ✓
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockComparison.map((r, index) => {
                      const isFg = r.itemKind === "finished_good";
                      const isOpen = expandedKeys.has(r.key);
                      const expected = r.system - r.reserved + r.returns;
                      const unexplained = r.theoretical - expected;
                      return (
                        <Fragment key={r.key}>
                          <TableRow
                            onClick={
                              isFg ? () => toggleExpanded(r.key) : undefined
                            }
                            className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${
                              isFg ? "cursor-pointer hover:bg-muted/40" : ""
                            }`}
                            style={{
                              animationDelay: `${Math.min(index * 40, 400)}ms`,
                            }}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {isFg ? (
                                  <ChevronRight
                                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                                      isOpen ? "rotate-90" : ""
                                    }`}
                                  />
                                ) : (
                                  <span className="w-4 shrink-0" />
                                )}
                                <div>
                                  <div className="font-medium">{r.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {r.code}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{r.kind}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtQty(r.theoretical)}
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtQty(r.system)}
                            </TableCell>
                            <TableCell
                              className={`text-right font-medium ${
                                r.diff < 0
                                  ? "text-destructive"
                                  : "text-emerald-700"
                              }`}
                            >
                              {r.diff > 0 ? "+" : ""}
                              {fmtQty(r.diff)}
                            </TableCell>
                          </TableRow>
                          {isFg && isOpen && (
                            <TableRow className="bg-muted/30 duration-200 animate-in fade-in-0 slide-in-from-top-1">
                              <TableCell colSpan={5} className="py-3">
                                <div className="mx-auto max-w-md rounded-lg border bg-background p-3">
                                  <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                    Puente físico → teórico
                                  </div>
                                  <dl className="space-y-1 text-sm">
                                    <BridgeRow
                                      label="Sistema (físico / bodega)"
                                      value={r.system}
                                      tone="strong"
                                    />
                                    <BridgeRow
                                      label="− Facturado no despachado"
                                      value={r.reserved}
                                    />
                                    <BridgeRow
                                      label="+ Devoluciones NC (período)"
                                      value={r.returns}
                                    />
                                    <div className="my-1 border-t" />
                                    <BridgeRow
                                      label="= Teórico esperado"
                                      value={expected}
                                      tone="strong"
                                    />
                                    <BridgeRow
                                      label="Teórico (costeo)"
                                      value={r.theoretical}
                                      tone="strong"
                                    />
                                    <BridgeRow
                                      label="Diferencia sin explicar"
                                      value={unexplained}
                                      tone="warn"
                                    />
                                  </dl>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* D) Supporting documents — open the reception PDF inline */}
        <TabsContent value="documentos">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Documentos de respaldo de compras
              </CardTitle>
              <CardDescription>
                El PDF de la recepción se abre aquí mismo — vital para resolver
                discrepancias de cantidades o costos al instante.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-28">Fecha</TableHead>
                    <TableHead>Ítem</TableHead>
                    <TableHead className="w-32">Documento</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-32 text-right">Respaldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchaseDocs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-6 text-center text-muted-foreground"
                      >
                        Sin compras en el período
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchaseDocs.map((m, index) => (
                      <TableRow
                        key={m.id}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                      >
                        <TableCell>
                          {dayjs(m.movement_date).format("DD/MM/YYYY")}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {m.raw_materials?.name ??
                              m.oil_types?.name ??
                              m.products?.short_name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {m.notes ?? ""}
                          </div>
                        </TableCell>
                        <TableCell>{m.document_number ?? "—"}</TableCell>
                        <TableCell className="text-right">
                          {fmtCLP(m.total_cost_clp)}
                        </TableCell>
                        <TableCell className="text-right">
                          {m.attachment_url ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewingPdf(m.attachment_url)}
                            >
                              <FileText className="mr-1 h-4 w-4" />
                              Ver PDF
                            </Button>
                          ) : (
                            <span className="flex items-center justify-end gap-1 text-sm text-amber-600">
                              <FileX className="h-4 w-4" />
                              Falta
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PdfViewerDialog path={viewingPdf} onClose={() => setViewingPdf(null)} />
    </PageContainer>
  );
}
